"use client";

import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { MessageCircle, Phone, MapPinned, Mail } from "lucide-react";
import {
  SITE_PHONE_E164,
  SITE_PHONE_DISPLAY,
  SITE_SECONDARY_PHONE_E164,
  SITE_SECONDARY_PHONE_DISPLAY,
  SITE_WHATSAPP_URL_DEFAULT,
  GOOGLE_MAPS_DIRECTIONS_URL,
} from "@/lib/site-config";
import { listCourseRecords } from "@/lib/courses-catalog";
import { useState, type FormEvent } from "react";

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleEnquiry(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      phone: String(fd.get("phone") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      course: String(fd.get("course") ?? "").trim(),
      message: String(fd.get("message") ?? "").trim(),
    };

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Enquiry sent successfully! We will get back to you soon.");
        form.reset();
      } else {
        alert("Failed to send enquiry. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col overflow-hidden relative">
      <Navbar />

      <main className="flex-1 pt-20 relative z-10">
        <section className="relative min-h-[70vh] flex items-center justify-center pt-12 pb-20 overflow-hidden bg-background sm:pt-20">
          <div className="absolute inset-0 bg-grid opacity-50 dark:opacity-20 z-0 mask-image-gradient" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-mont-violet/20 dark:bg-mont-violet/40 rounded-full blur-[140px] pointer-events-none z-0" />

          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto rounded-3xl border border-transparent bg-transparent sm:border-border sm:bg-card/30 p-0 sm:p-8 md:p-10 transition-all duration-300">
              <div className="mb-10 px-2 sm:px-0">
                <h1 className="text-4xl md:text-6xl font-bold font-heading mb-4 text-foreground tracking-tight">Contact Us</h1>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                  Reach out for course counseling, upcoming batch details, and fee support — always quoted in INR to match the catalog.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                <div className="flex flex-col gap-6 justify-start px-2 sm:px-0">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-foreground font-heading mb-3">Contact Information</h2>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        Have questions? Reach out to us through any of the channels below. We are here to help you accelerate your data career.
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* Phone card */}
                      <div className="flex items-center gap-4 p-4 rounded-2xl border border-border/50 bg-card/40 backdrop-blur-sm shadow-sm">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-mont-violet/10 text-mont-violet">
                          <Phone size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-muted-foreground">Phone Support</p>
                          <p className="text-xs sm:text-sm font-bold text-foreground mt-0.5 truncate">
                            {SITE_PHONE_DISPLAY} / {SITE_SECONDARY_PHONE_DISPLAY}
                          </p>
                        </div>
                      </div>

                      {/* Email card */}
                      <div className="flex items-center gap-4 p-4 rounded-2xl border border-border/50 bg-card/40 backdrop-blur-sm shadow-sm">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-mont-violet/10 text-mont-violet">
                          <Mail size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-muted-foreground">Email Address</p>
                          <p className="text-xs sm:text-sm font-bold text-foreground mt-0.5 truncate">
                            contact@mont2academy.com
                          </p>
                        </div>
                      </div>

                      {/* Address card */}
                      <div className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 bg-card/40 backdrop-blur-sm shadow-sm">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-mont-violet/10 text-mont-violet mt-0.5">
                          <MapPinned size={18} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground">Classroom Center</p>
                          <p className="text-xs sm:text-sm font-medium text-foreground mt-0.5 leading-relaxed">
                            First Floor, LIC Building, Near Mehrauli Bus Stand, South Delhi, New Delhi - 110030
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Link href={`tel:${SITE_PHONE_E164}`} className="flex-1">
                        <Button variant="outline" className="w-full h-12 rounded-xl border-border/80 font-semibold shadow-sm hover:bg-muted/40 transition-colors">
                          <Phone className="mr-2 h-4 w-4 text-mont-violet" />
                          Call Us
                        </Button>
                      </Link>
                      <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button variant="outline" className="w-full h-12 rounded-xl border-border/80 font-semibold shadow-sm hover:bg-muted/40 transition-colors">
                          <MessageCircle className="mr-2 h-4 w-4 text-[#22c55e]" />
                          WhatsApp
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleEnquiry} className="space-y-4 rounded-2xl border border-border/80 p-5 sm:p-8 bg-card/60 shadow-lg backdrop-blur-sm h-fit">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground font-heading mb-1">Enquiry Form</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-4">Leave your details and we&apos;ll get back to you.</p>
                  </div>
                  <input name="name" required className="w-full h-11 rounded-xl border border-border bg-background/80 px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-mont-violet/50" placeholder="Full Name" />
                  <input name="phone" required type="tel" className="w-full h-11 rounded-xl border border-border bg-background/80 px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-mont-violet/50" placeholder="Phone Number" />
                  <input name="email" required type="email" className="w-full h-11 rounded-xl border border-border bg-background/80 px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-mont-violet/50" placeholder="Email" />
                  <select name="course" required defaultValue="" className="w-full h-11 rounded-xl border border-border bg-background/80 px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-mont-violet/50">
                    <option value="" disabled hidden>Course Interested In</option>
                    {listCourseRecords().map((c) => (
                      <option key={c.id} value={c.title}>{c.title}</option>
                    ))}
                  </select>
                  <textarea name="message" className="w-full rounded-xl border border-border bg-background/80 px-4 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-mont-violet/50 min-h-[100px]" placeholder="Message" />
                  <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-xl bg-gradient-primary text-white font-semibold border-0 hover:opacity-90 shadow-[0_0_20px_rgba(108,92,231,0.3)] disabled:opacity-50">
                    {isSubmitting ? "Sending..." : "Submit Enquiry"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
